import { Suspense, memo, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { LoadingPage } from "@/components/ui/LoadingPage";
import { useLanguage } from "@/contexts/LanguageContext";
import { lazyImport, preloadComponents } from "@/utils/lazyImport";
import ScrollToTop from "@/components/ui/ScrollToTop";
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from "@/components/ui/ErrorFallback";
import { ReactRouterEnhancer } from "@/components/seo/ReactRouterEnhancer";
import { SSORedirectManager } from "./utils/ssoRedirect";

// Initialiser le SSO au chargement de l'application
SSORedirectManager.initSSO();

// Import frequently accessed pages directly 
import CreatorPanel from "@/pages/creator/CreatorPanel";
import Index from "@/pages/Index";
import AllChannels from "@/pages/AllChannels";
import AllClips from "@/pages/AllClips";
import AllPlaylists from "@/pages/AllPlaylists";
import LamourCestSurcote from './pages/LamourCestSurcote';
import DerivativesGrid from '@/pages/DerivativesGrid';
import PressRoom from "./pages/PressRoom";
import SitemapXml from "./pages/SitemapXml";

// Create optimized lazy imports
const Login = lazyImport(() => import("@/pages/auth/Login"));
const Signup = lazyImport(() => import("@/pages/auth/Signup"));
const ForgotPassword = lazyImport(() => import("@/pages/auth/ForgotPassword"));
const ResetPassword = lazyImport(() => import("@/pages/auth/ResetPassword"));

const ChannelDetail = lazyImport(() => import("@/pages/ChannelDetail"));
const ClipDetail = lazyImport(() => import("@/pages/ClipDetail"));
const PlaylistDetail = lazyImport(() => import("@/pages/PlaylistDetail"));
const DerivativeDetail = lazyImport(() => import("@/pages/DerivativeDetail"));
const CreateDerivative = lazyImport(() => import("@/pages/CreateDerivative"));
const EditDerivative = lazyImport(() => import("@/pages/CreateDerivative"));
const CreatorProfile = lazyImport(() => import("@/pages/CreatorProfile"));
const Creator = lazyImport(() => import("@/pages/Creator"));
const AllCreators = lazyImport(() => import("@/pages/AllCreators"));
const AdminDashboard = lazyImport(() => import("@/pages/admin/AdminDashboard"));
const AdminChannelDetail = lazyImport(() => import("@/pages/admin/components/AdminChannelDetail"));
const NotFound = lazyImport(() => import("@/pages/NotFound"));
const PrivacyPolicy = lazyImport(() => import("./pages/PrivacyPolicy"));
const LegalNotice = lazyImport(() => import("./pages/LegalNotice"));
const ContactUs = lazyImport(() => import("./pages/ContactUs"));
const ChallengeDetail = lazyImport(() => import("./pages/ChallengeDetail"));

// Use route-based prefetching for better navigation UX
const usePrefetchRoutes = () => {
  const location = useLocation();
  
  useEffect(() => {
    if (location.pathname === '/auth/login') {
      preloadComponents([
        () => import('./pages/auth/Signup'),
        () => import('./pages/auth/ForgotPassword'),
        () => import('./pages/Index')
      ]);
    } else if (location.pathname === '/auth/signup') {
      preloadComponents([
        () => import('./pages/auth/Login'),
        () => import('./pages/Index')
      ]);
    } else if (location.pathname === '/') {
      preloadComponents([
        () => import('./pages/AllChannels'),
        () => import('./pages/AllClips')
      ]);
    } else if (location.pathname.includes('/creator/')) {
      preloadComponents([
        () => import('./pages/DerivativesGrid')
      ]);
    } else if (location.pathname.startsWith('/auth/reset-password')) {
      preloadComponents([
        () => import('./pages/auth/Login')
      ]);
    } else if (location.pathname === '/derivatives') {
      preloadComponents([
        () => import('./pages/DerivativeDetail')
      ]);
    }
  }, [location.pathname]);
};

const OptimizedFallback = memo(() => <LoadingPage />);
OptimizedFallback.displayName = "OptimizedFallback";

const AuthRoute = memo(({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isAdmin } = useAuth();
  
  if (isAuthenticated) {
    return <Navigate to={isAdmin ? "/admin" : "/creator/panel"} replace />;
  }
  
  return <>{children}</>;
});
AuthRoute.displayName = "AuthRoute";

function App() {
  const { isAuthenticated, isAdmin, isCreator } = useAuth();
  usePrefetchRoutes();
  const { t, language } = useLanguage();
  
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // Reset the error boundary when the user clicks "try again"
        console.log("Error boundary reset");
      }}
    >
      <ReactRouterEnhancer>
        <ScrollToTop />
        <Toaster />
        <Suspense fallback={<OptimizedFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            
            <Route path="/explore" element={<Navigate to="/" replace />} />
            
            {/* Single route that works for both languages */}
            <Route path="/press-room" element={<PressRoom />} />
            <Route path="/espace-presse" element={<PressRoom />} />
            
            <Route path="/LamourCestSurcote" element={<LamourCestSurcote />} />
            <Route path="/challenge/:id" element={<ChallengeDetail />} />
            
            {/* Dynamic sitemap endpoint */}
            <Route path="/sitemap.xml" element={<SitemapXml />} />
            
            <Route path="/dashboard" element={
              isAuthenticated ? (
                isAdmin ? <Navigate to="/admin" replace /> : 
                <Navigate to="/creator/panel" replace />
              ) : <Navigate to="/auth/login" replace />
            } />
            
            <Route path="/channels" element={<AllChannels />} />
            <Route path="/channel/:id" element={<ChannelDetail />} />
            <Route path="/clips" element={<AllClips />} />
            <Route path="/clip/:id" element={<ClipDetail />} />
            <Route path="/playlists" element={<AllPlaylists />} />
            <Route path="/playlist/:id" element={<PlaylistDetail />} />
            <Route path="/derivatives" element={<DerivativesGrid />} />
            <Route path="/derivative/:id" element={<DerivativeDetail />} />
            <Route path="/create-derivative" element={
              isAuthenticated ? <CreateDerivative /> : <Navigate to="/auth/login" replace />
            } />
            <Route path="/edit-derivative/:id" element={
              isAuthenticated ? <EditDerivative /> : <Navigate to="/auth/login" replace />
            } />
            <Route path="/creator-profile/:id" element={<CreatorProfile />} />
            <Route path="/creator" element={<Creator />} />
            <Route path="/creators" element={<AllCreators />} />
            <Route path="/creator/panel" element={
              isAuthenticated ? (
                isAdmin ? <Navigate to="/admin" replace /> : <CreatorPanel />
              ) : <Navigate to="/auth/login" replace />
            } />
            
            <Route path="/admin" element={
              isAuthenticated && isAdmin ? <AdminDashboard /> : <Navigate to="/" replace />
            } />
            <Route path="/admin/channel/:id" element={
              isAuthenticated && isAdmin ? <AdminChannelDetail /> : <Navigate to="/" replace />
            } />
            
            <Route path="/auth/login" element={
              <AuthRoute>
                <Login />
              </AuthRoute>
            } />
            <Route path="/auth/signup" element={
              <AuthRoute>
                <Signup />
              </AuthRoute>
            } />
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/legal-notice" element={<LegalNotice />} />
            <Route path="/contact-us" element={<ContactUs />} />
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ReactRouterEnhancer>
    </ErrorBoundary>
  );
}

export default memo(App);
